// In dev the configured API URL is often a LAN IP (so a phone on the same
// Wi-Fi can hit the backend). Opening the frontend at a different host
// (e.g. localhost on the dev machine) would make the API request cross-site
// and the `jq4_refresh` cookie — set with `sameSite=strict` — wouldn't be
// sent, breaking session rehydration on reload. To keep both flows working
// we rewrite the hostname to match the current page in dev only.
function resolveApiBaseUrl(): string {
  const configured = import.meta.env.VITE_API_BASE_URL as string;
  if (!import.meta.env.DEV || typeof window === 'undefined') return configured;
  try {
    const url = new URL(configured);
    if (url.hostname === window.location.hostname) return configured;
    url.hostname = window.location.hostname;
    return url.toString().replace(/\/$/, '');
  } catch {
    return configured;
  }
}

// Vite inlina esto en BUILD time, no en runtime: si VITE_APP_VERSION no esta
// definida al construir la imagen, queda `undefined` para siempre y Sentry
// omite el release. El placeholder '0.0.0' del package.json se trata como NO
// seteada — mandar un release inventado agrupa todos los deploys bajo la misma
// etiqueta y vuelve inutil la atribucion. Espeja apps/backend/src/shared/version.ts.
function resolveAppVersion(): string | undefined {
  const raw = (import.meta.env.VITE_APP_VERSION as string | undefined)?.trim();
  return raw && raw !== '0.0.0' ? raw : undefined;
}

// ADR-017 / design D17 — build-time kill switch. 'on' (default): offline
// capture allowed. 'drain': the sync engine (slice 7) keeps draining any
// already-queued items but new offline captures are refused — check-in
// falls back to requiring connectivity. Unused before slice 6/7 (no capture
// path exists yet), exposed here now so those slices don't need to touch
// env.ts again.
function resolveOfflineCapture(): 'on' | 'drain' {
  return import.meta.env.VITE_OFFLINE_CAPTURE === 'drain' ? 'drain' : 'on';
}

export const env = {
  apiBaseUrl: resolveApiBaseUrl(),
  offlineCapture: resolveOfflineCapture(),
  // /auth/* must be same-origin in production so the httpOnly `jq4_refresh`
  // cookie is first-party. Frontend and backend are cross-site on
  // *.up.railway.app (a public suffix), so a third-party refresh cookie gets
  // dropped by the browser and every reload/bootstrap loses the session.
  // `vite preview` proxies /auth to the backend (see vite.config.ts). In dev
  // the API host already matches the page host (see resolveApiBaseUrl), so the
  // cookie is same-site there and we keep hitting the backend directly.
  authBaseUrl: import.meta.env.DEV ? resolveApiBaseUrl() : '',
  sentryDsn: import.meta.env.VITE_SENTRY_DSN,
  sentryEnv: import.meta.env.VITE_SENTRY_ENV ?? 'local',
  appVersion: resolveAppVersion(),
};
